import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Download, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import JobHeader from '../components/job/JobHeader';
import JobEquipmentSection from '../components/job/JobEquipmentSection';
import TechAssignmentSection from '../components/job/TechAssignmentSection';
import EquipAssignmentSection from '../components/job/EquipAssignmentSection';
import SchedulePlanSection from '../components/job/SchedulePlanSection';
import NotesSection from '../components/job/NotesSection';
import JobFormsSection from '../components/job/JobFormsSection';
import FieldEventsSection from '../components/job/FieldEventsSection';
import DateField, { isValidDateInput } from '../components/DateField';
import { extractErrorMessage } from '../utils/apiErrors';
import { useLetterheadExport } from '../components/reports/useLetterheadExport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Metadata for statuses that prompt for a date when selected. Only applies to
// default statuses; custom statuses don't get date prompts.
const STATUS_DATE_META = {
  waiting_parts: { dateLabel: 'Expected Parts Arrival' },
  waiting_approval: { dateLabel: 'Approval Deadline' },
  subcontractor: { dateLabel: 'Expected Return' },
};

function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { requestExport, exportFeedback, exporting } = useLetterheadExport('job-letterhead-export');
  const [job, setJob] = useState(null);
  const [notes, setNotes] = useState([]);
  const [techAssignments, setTechAssignments] = useState([]);
  const [equipAssignments, setEquipAssignments] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [showFormPicker, setShowFormPicker] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [formPickerIncludeAttachments, setFormPickerIncludeAttachments] = useState(false);

  // Count of PDF attachments across all notes — used to label the export menu.
  const notePdfCount = notes.reduce(
    (sum, n) => sum + (n.attachments || []).filter((a) => typeof a === 'string' && a.toLowerCase().endsWith('.pdf')).length,
    0
  );

  const canModify = user?.role === 'management' || user?.role === 'foreman';

  const loadData = useCallback(async () => {
    try {
      const [jobRes, notesRes, techRes, equipRes, techListRes, equipListRes] = await Promise.all([
        axios.get(`${API}/jobs/${id}`),
        axios.get(`${API}/notes/${id}`),
        axios.get(`${API}/assignments/${id}`),
        axios.get(`${API}/equipment-assignments/${id}`),
        axios.get(`${API}/technicians`),
        axios.get(`${API}/equipment`)
      ]);
      setJob(jobRes.data);
      setNotes(notesRes.data);
      setTechAssignments(techRes.data);
      setEquipAssignments(equipRes.data);
      setTechnicians(techListRes.data);
      setEquipment(equipListRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  }, [id]);

  useEffect(() => {
    // Clear stale state when navigating between jobs (e.g., clicking one
    // deadline notification then another). Otherwise the UI briefly shows
    // the previous job's data — including its just-edited end_date — under
    // the new job's URL, which invites accidental updates to the wrong job.
    setJob(null);
    setNotes([]);
    setTechAssignments([]);
    setEquipAssignments([]);
    setFormSubmissions([]);
    loadData();
  }, [loadData]);

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/jobs/${id}`, { status: newStatus });
      setJob({ ...job, status: newStatus });
      toast.success('Status updated');
    } catch (error) { toast.error('Failed'); }
  };

  const handleStatusDateChange = async (statusKey, dateValue) => {
    try {
      const newDates = { ...(job.status_dates || {}), [statusKey]: dateValue };
      // The backend also shifts the matching schedule phase (and its dependents),
      // so take the job it returns rather than patching state locally.
      const res = await axios.patch(`${API}/jobs/${id}`, { status_dates: newDates });
      setJob(res.data);
      toast.success('Date updated');
    } catch (error) { toast.error('Failed'); }
  };

  const handleChargeUpdate = async (amount) => {
    try {
      await axios.patch(`${API}/jobs/${id}`, { charge_amount: amount });
      setJob({ ...job, charge_amount: amount });
      toast.success('Charge amount saved');
    } catch (error) { toast.error('Failed to save charge amount'); }
  };

  const handleEndDateChange = async (newEndDate) => {
    try {
      // Preserve the time portion if it exists, otherwise default to end of day
      const endDateTime = job.end_date 
        ? `${newEndDate}T${job.end_date.slice(11) || '17:00:00'}`
        : `${newEndDate}T17:00:00`;
      await axios.patch(`${API}/jobs/${id}`, { end_date: endDateTime });
      setJob({ ...job, end_date: endDateTime });
      toast.success('End date updated');
    } catch (error) { toast.error('Failed to update end date'); }
  };

  const handleEditAssignment = (assignment, type) => {
    setEditingAssignment({ ...assignment, type });
    setAssignmentError('');
    setEditDialog(true);
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    if (!editingAssignment || savingAssignment) return;
    if (!isValidDateInput(editingAssignment.date)) {
      setAssignmentError('Choose a valid assignment date before saving.');
      return;
    }
    setSavingAssignment(true);
    setAssignmentError('');
    try {
      const endpoint = editingAssignment.type === 'tech' ? 'assignments' : 'equipment-assignments';
      const body = {
        date: editingAssignment.date,
        hours: editingAssignment.hours !== '' && editingAssignment.hours != null ? parseFloat(editingAssignment.hours) : null,
      };
      if (editingAssignment.type === 'tech') {
        body.overtime_hours = editingAssignment.overtime_hours !== '' && editingAssignment.overtime_hours != null
          ? parseFloat(editingAssignment.overtime_hours)
          : null;
      }
      const res = await axios.patch(`${API}/${endpoint}/${editingAssignment.id}`, body);
      const updateRows = (rows) => rows.map((row) => row.id === res.data.id ? res.data : row);
      if (editingAssignment.type === 'tech') setTechAssignments(updateRows);
      else setEquipAssignments(updateRows);
      await loadData();
      setEditDialog(false);
      toast.success('Assignment updated');
    } catch (error) { setAssignmentError(extractErrorMessage(error, 'Unable to save the assignment')); }
    finally { setSavingAssignment(false); }
  };

  const downloadReport = async (format, submissionIds = [], opts = {}) => {
    try {
      toast.info(`Generating ${format.toUpperCase()}...`);
      const qs = [];
      if (submissionIds.length > 0) {
        qs.push('include_forms=true');
        qs.push('submission_ids=' + submissionIds.join(','));
      }
      if (opts.includeAttachments) {
        qs.push('include_attachments=true');
      }
      const params = qs.length > 0 ? '?' + qs.join('&') : '';
      const response = await axios.get(`${API}/jobs/${id}/export/${format}${params}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Job_${job.job_number}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded!`);
    } catch (error) {
      throw error;
    }
  };

  const handleExport = (...args) => { setShowExportMenu(false); return requestExport(() => downloadReport(...args)); };

  const openFormPicker = async () => {
    setShowExportMenu(false);
    try {
      const res = await axios.get(`${API}/jobs/${id}/pdf-forms`);
      const subs = res.data.submissions || [];
      const templates = res.data.templates || [];
      // Enrich submissions with template name
      const enriched = subs.map(s => ({
        ...s,
        template_name: templates.find(t => t.id === s.template_id)?.name || s.template_name
      }));
      setFormSubmissions(enriched);
      setSelectedSubmissions(enriched.map(s => s.id)); // Select all by default
      setShowFormPicker(true);
    } catch {
      toast.error('Failed to load form submissions');
    }
  };

  const handleExportWithForms = () => {
    setShowFormPicker(false);
    handleExport('pdf', selectedSubmissions, { includeAttachments: formPickerIncludeAttachments });
  };

  const toggleSubmission = (subId) => {
    setSelectedSubmissions(prev =>
      prev.includes(subId) ? prev.filter(id => id !== subId) : [...prev, subId]
    );
  };

  const toggleAllSubmissions = () => {
    if (selectedSubmissions.length === formSubmissions.length) {
      setSelectedSubmissions([]);
    } else {
      setSelectedSubmissions(formSubmissions.map(s => s.id));
    }
  };

  // Check if current user (technician) is assigned to this job
  // Compare names case-insensitively and trim whitespace
  const isTechAssignedToJob = user?.role === 'technician' && techAssignments.some(
    a => a.tech_name?.toLowerCase().trim() === user.name?.toLowerCase().trim()
  );
  
  // Technicians can assign equipment only if they are assigned to the job
  const canAssignEquipment = canModify || isTechAssignedToJob;

  if (!job) {
    return <DashboardLayout><div className="flex items-center justify-center h-64 text-slate-600">Loading...</div></DashboardLayout>;
  }

  const currentStatus = job.status ? { value: job.status, ...STATUS_DATE_META[job.status], hasDate: !!STATUS_DATE_META[job.status] } : null;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="job-detail-page">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/jobs')} className="border-slate-300" data-testid="back-to-jobs">
            <ArrowLeft size={20} className="mr-2" />Back to Jobs
          </Button>
          <div className="flex flex-wrap gap-2">
            <Popover open={showExportMenu} onOpenChange={setShowExportMenu}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-slate-300" disabled={exporting} data-testid="export-pdf-btn">
                  <FileText size={16} className="mr-2" />Export PDF<ChevronDown size={14} className="ml-1" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-1" align="end">
                <button
                  onClick={() => handleExport('pdf')}
                  className="w-full text-left px-3 py-2 text-sm rounded hover:bg-slate-100 transition-colors"
                  data-testid="export-pdf-only"
                >
                  Job Report Only
                </button>
                {notePdfCount > 0 && (
                  <button
                    onClick={() => handleExport('pdf', [], { includeAttachments: true })}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-slate-100 transition-colors"
                    data-testid="export-pdf-with-attachments"
                  >
                    Include PDF Attachments ({notePdfCount})
                  </button>
                )}
                <button
                  onClick={openFormPicker}
                  className="w-full text-left px-3 py-2 text-sm rounded hover:bg-slate-100 transition-colors"
                  data-testid="export-pdf-with-forms"
                >
                  Include Filled Forms...
                </button>
              </PopoverContent>
            </Popover>
            <Button variant="outline" onClick={() => handleExport('docx')} disabled={exporting} className="border-slate-300" data-testid="export-docx-btn">
              <Download size={16} className="mr-2" />Export Word
            </Button>
          </div>
        </div>

        {exportFeedback}
        <JobHeader job={job} currentStatus={currentStatus} canModify={canModify} onStatusChange={handleStatusChange} onDateChange={handleStatusDateChange} onChargeUpdate={handleChargeUpdate} onEndDateChange={handleEndDateChange} />
        <JobEquipmentSection jobId={id} job={job} equipment={job.equipment || []} canModify={canModify} onReload={loadData} />
        <SchedulePlanSection jobId={id} segments={job.schedule_plan || []} jobEndDate={job.end_date} canModify={canModify} onReload={loadData} />
        <TechAssignmentSection jobId={id} assignments={techAssignments} technicians={technicians} canModify={canModify} onReload={loadData} onEdit={handleEditAssignment} />
        <EquipAssignmentSection jobId={id} assignments={equipAssignments} equipment={equipment} canModify={canAssignEquipment} onReload={loadData} onEdit={handleEditAssignment} />
        <JobFormsSection jobId={id} canModify={canModify} />
        <FieldEventsSection jobId={id} />
        <NotesSection jobId={id} notes={notes} onReload={loadData} equipment={job.equipment || []} />

        <Dialog open={editDialog} onOpenChange={(open) => !savingAssignment && setEditDialog(open)}>
          <DialogContent className="grid-cols-[minmax(0,1fr)] max-h-[90dvh] overflow-y-auto" data-testid="edit-assignment-dialog">
            <DialogHeader><DialogTitle>Edit Assignment</DialogTitle><DialogDescription className="sr-only">Update the date and hours for this assignment.</DialogDescription></DialogHeader>
            {editingAssignment && (
              <form onSubmit={handleSaveEdit} className="space-y-4" data-testid="edit-assignment-form">
                <div><Label>Date</Label><DateField allowTextEntry inlineCalendar disabled={savingAssignment} value={editingAssignment.date} onChange={(v) => { setEditingAssignment((prev) => ({...prev, date: v})); setAssignmentError(''); }} data-testid="edit-assignment-date" /></div>
                <div>
                  <Label>Hours</Label>
                  <Input 
                    type="number" 
                    step="0.25" 
                    min="0" 
                    placeholder="e.g. 2.5"
                    value={editingAssignment.hours ?? ''}
                    disabled={savingAssignment}
                    data-testid="edit-assignment-hours"
                    onChange={(e) => { const hours = e.target.value; setEditingAssignment((prev) => ({...prev, hours})); }}
                  />
                </div>
                {editingAssignment.type === 'tech' && (
                  <div>
                    <Label>Overtime Hours</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      placeholder="optional"
                      value={editingAssignment.overtime_hours ?? ''}
                      disabled={savingAssignment}
                      onChange={(e) => { const overtime_hours = e.target.value; setEditingAssignment((prev) => ({...prev, overtime_hours})); }}
                      data-testid="edit-overtime-hours"
                    />
                  </div>
                )}
                {assignmentError && <p role="alert" className="text-sm text-red-600" data-testid="edit-assignment-error">{assignmentError}</p>}
                <Button type="submit" disabled={savingAssignment || !isValidDateInput(editingAssignment.date)} className="w-full bg-slate-900" data-testid="save-edit-btn">{savingAssignment ? 'Saving...' : 'Save'}</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Form Picker Dialog */}
        <Dialog open={showFormPicker} onOpenChange={setShowFormPicker}>
          <DialogContent>
            <DialogHeader><DialogTitle>Select Forms to Include</DialogTitle></DialogHeader>
            {formSubmissions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No filled forms found for this job.</p>
            ) : (
              <div className="space-y-3" data-testid="form-picker">
                <label className="flex items-center gap-2 pb-2 border-b border-slate-200 cursor-pointer">
                  <Checkbox
                    checked={selectedSubmissions.length === formSubmissions.length}
                    onCheckedChange={toggleAllSubmissions}
                    data-testid="select-all-forms"
                  />
                  <span className="text-sm font-semibold text-slate-900">Select All</span>
                </label>
                {formSubmissions.map(sub => (
                  <label key={sub.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded p-1.5 -mx-1.5">
                    <Checkbox
                      checked={selectedSubmissions.includes(sub.id)}
                      onCheckedChange={() => toggleSubmission(sub.id)}
                      data-testid={`form-picker-${sub.id}`}
                    />
                    <div>
                      <p className="text-sm text-slate-900">{sub.template_name}</p>
                      <p className="text-xs text-slate-400">Filled by {sub.filled_by_name} · {new Date(sub.created_at).toLocaleDateString()}</p>
                    </div>
                  </label>
                ))}
                {notePdfCount > 0 && (
                  <label className="flex items-center gap-2 pt-3 mt-1 border-t border-slate-200 cursor-pointer">
                    <Checkbox
                      checked={formPickerIncludeAttachments}
                      onCheckedChange={(v) => setFormPickerIncludeAttachments(!!v)}
                      data-testid="form-picker-include-attachments"
                    />
                    <span className="text-sm text-slate-900">
                      Also include {notePdfCount} PDF attachment{notePdfCount !== 1 ? 's' : ''}
                    </span>
                  </label>
                )}
                <Button
                  onClick={handleExportWithForms}
                  disabled={selectedSubmissions.length === 0}
                  className="w-full bg-slate-900 mt-2"
                  data-testid="export-with-selected-forms"
                >
                  <FileText size={16} className="mr-2" />
                  Export with {selectedSubmissions.length} Form{selectedSubmissions.length !== 1 ? 's' : ''}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

export default JobDetail;
